import { Link, useLocation, useRouter } from "@tanstack/react-router";
import {
  LayoutDashboard,
  LogOut,
  Package,
  PanelLeft,
  PanelLeftClose,
  Settings,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { signOut, useSession } from "@/lib/auth-client";
import { cn } from "@/lib/utils";
import { useSidebar } from "./SidebarContext";

interface NavItem {
  label: string;
  icon: typeof LayoutDashboard;
  href: string;
  disabled?: boolean;
}

export function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const { data: session } = useSession();
  const router = useRouter();
  const location = useLocation();
  const { isMobile, mobileOpen, setMobileOpen } = useSidebar();

  const navItems = useMemo<NavItem[]>(() => {
    const items: NavItem[] = [
      { label: "My Dashboard", icon: LayoutDashboard, href: "/dashboard" },
      { label: "Items", icon: Package, href: "/dashboard/items" },
    ];

    if (session?.user?.role === "admin") {
      items.push({ label: "Settings", icon: Settings, href: "/dashboard/settings" });
    } else {
      items.push({ label: "Settings", icon: Settings, href: "#", disabled: true });
    }

    return items;
  }, [session?.user?.role]);

  async function handleSignOut() {
    await signOut();
    router.navigate({ to: "/login" });
  }

  function handleNavClick() {
    if (isMobile) {
      setMobileOpen(false);
    }
  }

  // On mobile, don't show collapsed state — always show full sidebar
  const showLabels = isMobile ? true : !collapsed;

  const sidebarContent = (
    <aside
      className={cn(
        "flex flex-col border-r border-sidebar-border bg-sidebar transition-all duration-200",
        isMobile ? "w-64 h-full" : collapsed ? "w-16" : "w-64",
      )}
    >
      {/* Brand */}
      <div className="flex h-16 items-center justify-between border-b border-sidebar-border px-4">
        {showLabels && (
          <span className="font-display text-lg font-bold text-sidebar-foreground truncate">
            Core Template
          </span>
        )}
        {!isMobile && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCollapsed(!collapsed)}
            className="text-sidebar-foreground shrink-0"
          >
            {collapsed ? <PanelLeft className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
          </Button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 p-2">
        {navItems.map((item) => {
          const isActive =
            item.href === "/dashboard"
              ? location.pathname === "/dashboard"
              : location.pathname.startsWith(item.href);

          return (
            <Link
              key={item.label}
              to={item.href}
              onClick={handleNavClick}
              className={cn(
                "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                isActive ? "bg-sidebar-accent text-sidebar-primary" : "text-sidebar-foreground/60",
                item.disabled && "pointer-events-none opacity-40",
                !item.disabled &&
                  !isActive &&
                  "hover:bg-sidebar-accent hover:text-sidebar-foreground",
              )}
            >
              <item.icon className="h-4 w-4 shrink-0" />
              {showLabels && <span>{item.label}</span>}
              {showLabels && item.disabled && (
                <span className="ml-auto text-[10px] uppercase tracking-wider text-muted-foreground">
                  Soon
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* User info + sign out */}
      <div className="border-t border-sidebar-border p-2">
        {showLabels && session?.user && (
          <div className="mb-2 px-3 py-2">
            <p className="text-sm font-medium text-sidebar-foreground truncate">
              {session.user.name}
            </p>
            <p className="text-xs text-muted-foreground truncate">{session.user.email}</p>
          </div>
        )}
        <button
          type="button"
          onClick={handleSignOut}
          className={cn(
            "flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-sidebar-foreground/60 hover:bg-sidebar-accent hover:text-sidebar-foreground transition-colors",
          )}
        >
          <LogOut className="h-4 w-4 shrink-0" />
          {showLabels && <span>Sign Out</span>}
        </button>
      </div>
    </aside>
  );

  // Mobile: overlay sidebar
  if (isMobile) {
    if (!mobileOpen) return null;

    return (
      <>
        {/* Backdrop */}
        <div className="fixed inset-0 z-40 bg-black/50" onClick={() => setMobileOpen(false)} />
        {/* Slide-out sidebar */}
        <div className="fixed left-0 top-0 z-50 h-full">{sidebarContent}</div>
      </>
    );
  }

  // Desktop: normal sidebar
  return sidebarContent;
}
